import { useEffect, useRef, useState } from "react";
import { api, apiErr, openFile } from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, X, FileText, Loader2 } from "lucide-react";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.gif,.doc,.docx,.xls,.xlsx,.txt";
const isImg = (f) => (f.content_type || "").startsWith("image/") && !/heic/i.test(f.content_type);
const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export async function uploadTeamFiles(channelId, files, onAdd) {
  for (const raw of files) {
    const file = raw.name && raw.name !== "image.png" ? raw
      : new File([raw], `pasted-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.${(raw.type.split("/")[1] || "png").replace("jpeg", "jpg")}`, { type: raw.type });
    if (file.size > 20 * 1024 * 1024) { toast.error(`${file.name} is over 20 MB`); continue; }
    const fd = new FormData();
    fd.append("file", file);
    try { const { data } = await api.post(`/team/channels/${channelId}/attachments`, fd); onAdd(data); }
    catch (err) { toast.error(`${file.name}: ${apiErr(err)}`); }
  }
}

// Paste-from-clipboard + drag-and-drop support for the chat panel.
export function useTeamDrop(channelId, onAdd) {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const run = async (files) => { if (!files.length || !channelId) return; setBusy(true); await uploadTeamFiles(channelId, files, onAdd); setBusy(false); };
  const hasFiles = (e) => [...(e.dataTransfer?.types || [])].includes("Files");
  return {
    dragging, busy,
    onPaste: (e) => { const files = [...(e.clipboardData?.files || [])]; if (files.length) { e.preventDefault(); run(files); } },
    dropProps: {
      onDragOver: (e) => { if (hasFiles(e)) { e.preventDefault(); setDragging(true); } },
      onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); },
      onDrop: (e) => { if (!hasFiles(e)) return; e.preventDefault(); setDragging(false); run([...e.dataTransfer.files]); },
    },
  };
}

export function DropOverlay({ show }) {
  if (!show) return null;
  return (
    <div data-testid="team-drop-overlay" className="pointer-events-none absolute inset-2 z-20 rounded-2xl border-2 border-dashed border-primary bg-primary/5 flex items-center justify-center">
      <div className="text-sm font-semibold text-primary inline-flex items-center gap-2"><Paperclip className="h-4 w-4" /> Drop images or documents to attach</div>
    </div>
  );
}

// Paperclip button: uploads picked files to the channel and reports them to the composer.
export function AttachButton({ channelId, onAdd, disabled, busy: extBusy }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    setBusy(true);
    await uploadTeamFiles(channelId, files, onAdd);
    setBusy(false);
  };
  return (
    <>
      <input ref={ref} type="file" multiple accept={ACCEPT} className="hidden" onChange={pick} data-testid="team-attach-input" />
      <button type="button" data-testid="team-attach" disabled={disabled || busy || extBusy} onClick={() => ref.current?.click()} title="Attach a document or image (max 20 MB) — you can also paste or drag files here"
        className="px-3 rounded-xl border border-slate-200 text-slate-500 hover:text-primary hover:border-primary/40 disabled:opacity-50">
        {busy || extBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
      </button>
    </>
  );
}

export function PendingAttachments({ files, onRemove }) {
  if (!files.length) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="team-pending-attachments">
      {files.map((f) => (
        <span key={f.id} data-testid={`team-pending-${f.id}`} className="inline-flex items-center gap-1.5 text-xs bg-slate-100 rounded-lg px-2 py-1">
          <FileText className="h-3.5 w-3.5 text-slate-500" /> <span className="max-w-[180px] truncate">{f.file_name}</span> <span className="text-slate-400">{kb(f.size)}</span>
          <button type="button" data-testid={`team-pending-remove-${f.id}`} onClick={() => onRemove(f.id)} className="text-slate-400 hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>
        </span>
      ))}
    </div>
  );
}

function ImageThumb({ f }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let url = "";
    api.get(`/team/files/${f.id}`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [f.id]);
  return (
    <button type="button" data-testid={`team-att-img-${f.id}`} onClick={() => openFile(`/team/files/${f.id}`)} className="block rounded-lg overflow-hidden border border-slate-200 bg-slate-50 hover:opacity-90">
      {src ? <img src={src} alt={f.file_name} className="max-h-48 max-w-[260px] object-contain" /> : <div className="h-24 w-36 animate-pulse" />}
    </button>
  );
}

export function MessageAttachments({ items }) {
  if (!items?.length) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-2">
      {items.map((f) => isImg(f) ? <ImageThumb key={f.id} f={f} /> : (
        <button key={f.id} type="button" data-testid={`team-att-file-${f.id}`} onClick={() => openFile(`/team/files/${f.id}`).catch((e) => toast.error(apiErr(e)))}
          className="inline-flex items-center gap-2 text-xs border border-slate-200 rounded-lg px-2.5 py-2 bg-white hover:border-primary/40">
          <FileText className="h-4 w-4 text-primary" /> <span className="max-w-[200px] truncate font-semibold text-slate-700">{f.file_name}</span> <span className="text-slate-400">{kb(f.size || 0)}</span>
        </button>
      ))}
    </div>
  );
}
